import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  FuRequestFilter,
  FuRequestListResponse,
} from '@/types/scheduling-api';

/**
 * POST /api/Appointment/Request
 *
 * Mirrors Flutter `SchedulingDataSourceImpl.getFollowupRequests`
 * (`scheduling_data_source_impl.dart:542-561`). Drives the FU Requests
 * tab (PROD-847 PR-1).
 *
 * Wraps `useQuery` over a POST because the response is deterministic
 * per `(orgId, practiceId, searchText, pageNumber, pageSize, isActive)`
 * tuple — cache-by-key works the same as a GET. Same pattern as
 * `useQuestionnaireDefinition`.
 *
 * **POST-as-read caching contract** (Samer PR #266 review §1):
 * because this is a POST being used as a list-fetch, the hook MUST
 * NOT re-fire on every tab switch or window focus. The project-wide
 * `QueryClient` config at
 * `src/app/providers/query-client.ts:33-37` enforces this globally:
 *   - `refetchOnWindowFocus: false`
 *   - `refetchOnReconnect: false`
 *   - `staleTime: 5 * 60_000` (5 minutes — refetches only on user
 *     action or mutation invalidation)
 * Belt-and-suspenders: the queryFn itself ALSO sets these inline so
 * the invariant survives any future change to the global defaults.
 * If the global config is ever loosened, this hook's behaviour
 * doesn't drift.
 *
 * Wire shape verified against 2026-05-20 dev curl (active-requests
 * view, `count: 1365`, populated `fuRequests[]`).
 *
 * Wire-shape rules (mirror Flutter verbatim):
 *   - `searchText: ""` (empty string), not null, when no search
 *   - `isActive: true` for active requests, `false` for cancelled
 *   - `pageNumber` is 1-based (1, 2, 3, ...)
 *
 * `enabled` gates on all required identity fields being non-nullish
 * so the queryFn never POSTs a `null` GUID. The runtime guard inside
 * the queryFn is defensive — same pattern as
 * `useQuestionnaireDefinition`.
 *
 * Forwards `signal` per project convention.
 */

export interface UseFollowUpRequestListParams {
  filter: FuRequestFilter;
  enabled?: boolean;
}

export function useFollowUpRequestList(
  params: UseFollowUpRequestListParams,
): UseQueryResult<FuRequestListResponse> {
  const { filter, enabled = true } = params;

  return useQuery<FuRequestListResponse>({
    queryKey: [
      'follow-up-requests',
      filter.organizationId,
      filter.practiceId,
      filter.searchText,
      filter.pageNumber,
      filter.pageSize,
      filter.isActive,
    ],
    enabled:
      enabled &&
      !!filter.organizationId &&
      !!filter.practiceId &&
      filter.pageNumber > 0 &&
      filter.pageSize > 0,
    // Explicit POST-as-read caching contract — see hook doc-block.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: ({ signal }) => {
      if (!filter.organizationId || !filter.practiceId) {
        // Should be unreachable — `enabled` guard above prevents this.
        throw new Error(
          'useFollowUpRequestList: queryFn invoked with missing identity ' +
            `(organizationId=${filter.organizationId}, practiceId=${filter.practiceId}). ` +
            'Enabled gate failed.',
        );
      }
      return post<FuRequestListResponse>(
        '/api/Appointment/Request',
        filter,
        { signal },
      );
    },
  });
}
